import moment from 'moment-timezone';
import { GetAppearance } from 'src/utils/CommonFunctions';

/**
 * Shared display formatting for the Latest Activity dropdown used on both the quote and order
 * listing rows (QuoteCustomerRow.tsx / NewOrderRow.tsx) and their respective detail pages.
 */
const formatActivityTime = (dateTime: string): string => {
    const appearance = GetAppearance();
    const tz = appearance?.timezone || moment.tz.guess();
    const now = moment.tz(tz);
    const target = moment.utc(dateTime).tz(tz);
    const diffSeconds = now.diff(target, 'seconds');
    const diffMinutes = now.diff(target, 'minutes');
    const diffHours = now.diff(target, 'hours');

    if (diffSeconds < 60) return `${diffSeconds} sec${diffSeconds !== 1 ? 's' : ''} ago`;
    if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;

    const yesterday = moment.tz(tz).subtract(1, 'day').startOf('day');

    if (target.clone().startOf('day').isSame(yesterday, 'day')) return `Yesterday, ${target.format('h:mm A')}`;

    return target.format('MMMM Do, h:mm A');
};

const resolveActivityMessage = (message: string, msgParams: Record<string, any> = {}): string => {
    if (!message) return '';

    return message
        .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
            if (key === 'userId') return '';

            return key in msgParams ? String(msgParams[key] ?? '') : `{{${key}}}`;
        })
        .replace(/\s{2,}/g, ' ')
        .trim();
};

/**
 * Normalizes a changed-field label for display.
 *
 * Field names reach the feed in whatever shape the module that recorded them used — snake_case
 * ("delivery_time"), camelCase ("phoneExtension") or bare lowercase ("order status") — because
 * they come straight off the activity payload, not from a display map. Rendering them raw put
 * underscores and lower-case column names in front of the customer. Every consumer formats
 * through here so the same field can't read differently on two screens.
 *
 * Tokens that are already all-caps (BOL, PO, ETA, MC) keep their casing instead of becoming "Bol",
 * and the same words arriving lower-case in a slug ("po_number") are restored to it.
 */
// Freight/industry abbreviations that must never be sentence-cased ("Po Number", "Bol Number").
const FIELD_LABEL_ACRONYMS = new Set(['bol', 'po', 'pro', 'mc', 'dot', 'usdot', 'eta', 'etd', 'id', 'ltl', 'ftl', 'vin', 'ref']);

export const formatActivityFieldLabel = (raw: string): string => {
    if (!raw) return '';

    return (
        raw
            .replace(/[_-]+/g, ' ')
            // camelCase / PascalCase → separate words. Two passes so a run of capitals stays together:
            // "phoneExtension" → "phone Extension", "BOLNumber" → "BOL Number".
            .replace(/([a-z\d])([A-Z])/g, '$1 $2')
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .replace(/\s{2,}/g, ' ')
            .trim()
            .split(' ')
            .map((word) => {
                if (FIELD_LABEL_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();

                return word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ')
    );
};

/**
 * Title-cases a machine slug that reached the feed as a raw stored VALUE — "ready_time" →
 * "Ready Time", "flexible" → "Flexible", "ltl" → "LTL".
 *
 * Activity changes record what was written to the column, not what the form displayed, so enum
 * values (window type, sensitivity, order type, status) arrive snake_case / lower-case and used to
 * render that way in front of the customer. Shares FIELD_LABEL_ACRONYMS with the field-label
 * formatter above so "ltl" can't read as "Ltl" on one screen and "LTL" on another.
 *
 * Same slug test WAL's ActivityFeedCmp uses (SNAKE_CASE_REGEX there), so a value can't humanize on
 * one platform and stay raw on the other: must start lower-case and carry only lower-case letters,
 * digits and underscores. Anything with a space, punctuation or an existing capital (dates,
 * amounts, emails, phone numbers, reference codes, free text) is returned untouched.
 */
const ENUM_SLUG_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

export const formatActivitySlugValue = (raw: string): string => {
    if (!raw || !ENUM_SLUG_RE.test(raw)) return raw;

    return raw
        .split('_')
        .map((word) => (FIELD_LABEL_ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');
};

const parseActivityChanges = (raw?: any): { field: string }[] => {
    if (!raw) return [];

    if (Array.isArray(raw)) {
        return raw.map((c: any) => ({ field: formatActivityFieldLabel(c?.field ?? '') })).filter((c) => c.field);
    }

    return Object.keys(raw).map((field) => ({ field: formatActivityFieldLabel(field) }));
};

// Joins a list of changed-field labels into a natural-reading phrase:
// ["A"] -> "A" | ["A","B"] -> "A and B" | ["A","B","C"] -> "A, B, and C"
const formatFieldList = (items: string[]): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;

    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const getActivityDisplay = (activity: any): { title: string; dotColor: string; entityLabel: 'Order' | 'Quote' } => {
    const name = (activity?.activityName || '').toLowerCase();
    // A single-entity activity call can legitimately mix in the other type — e.g. a converted
    // quote's trail includes the resulting order's activities (see quote.service.ts
    // getRelatedActivityConditions) — and the dashboard feed always mixes both. Always label by
    // the activity's own recorded `type`, never assume based on which screen triggered the fetch.
    const entityLabel: 'Order' | 'Quote' = activity?.type === 'order' ? 'Order' : 'Quote';

    if (activity?.isUnreadMessages || name.includes('message')) return { title: 'New Messages', dotColor: 'bg-primary', entityLabel };
    if (name.includes('no_rates') || name.includes('no rates')) return { title: 'No Rates Received', dotColor: 'bg-fgErrorPrimary', entityLabel };
    // Bug fix — quote_rate_created_by_user (a rep providing a carrier rate on an existing quote,
    // NOT the quote itself being created) contains "created" as a substring, so it must be checked
    // BEFORE the generic `created` branch below or it wrongly renders as "Quote Created".
    if (name.includes('rate_created')) return { title: 'Rate Added', dotColor: 'bg-successSecondary', entityLabel };
    if (name.includes('created')) return { title: `${entityLabel} Created`, dotColor: 'bg-successSecondary', entityLabel };
    // "Booked" matches the customer-facing wording used in the resolved message ("booked quote
    // {{code}}") and the product's "Book Order" flow — "Converted" was internal jargon leaking through.
    if (name.includes('booked')) return { title: `${entityLabel} Booked`, dotColor: 'bg-successSecondary', entityLabel };
    if (name.includes('sent')) return { title: `${entityLabel} Sent`, dotColor: 'bg-successSecondary', entityLabel };
    if (name.includes('carrier_assigned')) return { title: 'Carrier Assigned', dotColor: 'bg-successSecondary', entityLabel };
    if (name.includes('re_quote')) return { title: 'Re-Quote Requested', dotColor: 'bg-primary', entityLabel };
    // quote_duplicated_by_customer — the customer actually duplicated the quote through the portal
    // (a new quote exists). Must be checked BEFORE the `duplicate` branch below, which covers
    // quote_duplicate_requested_by_customer (asking WAL to do it) and would otherwise swallow it.
    if (name.includes('duplicated')) return { title: `${entityLabel} Duplicated`, dotColor: 'bg-successSecondary', entityLabel };
    if (name.includes('duplicate')) return { title: 'Duplicate Requested', dotColor: 'bg-primary', entityLabel };
    if (name.includes('note') || name.includes('post')) return { title: 'Note Added', dotColor: 'bg-primary', entityLabel };
    if (name.includes('tag')) return { title: 'Tags Updated', dotColor: 'bg-primary', entityLabel };
    if (name.includes('status')) return { title: 'Status Updated', dotColor: 'bg-primary', entityLabel };
    if (name.includes('rate')) return { title: 'Rate Updated', dotColor: 'bg-primary', entityLabel };
    if (name.includes('updated')) return { title: `${entityLabel} Updated`, dotColor: 'bg-primary', entityLabel };

    // Last-resort fallback for any activityName not yet mapped above — strip the actor
    // suffix (e.g. "_by_customer") so we never surface raw slug wording like
    // "Order Document Uploaded By Customer" to the customer.
    const cleanedName = name.replace(/_by_(customer|user|system|rule_set|private_carrier)$/, '');
    // Title-case whichever source wins — activityLabel can arrive lowercase (e.g. "update location").
    const fallbackTitle = (activity?.activityLabel || cleanedName.replace(/_/g, ' ') || 'Activity').replace(/\b\w/g, (c: string) => c.toUpperCase());
    const dotColor = activity?.activityStatus === 'success' ? 'bg-successSecondary' : activity?.activityStatus === 'failed' ? 'bg-fgErrorPrimary' : 'bg-primary';

    return { title: fallbackTitle, dotColor, entityLabel };
};

/**
 * Maps raw activity records (from getQuoteActivities, shared by quote and order entityTypes) into
 * the IActivityDisplayItem shape ActivityDropdownList renders.
 */
export const mapActivitiesToDisplayItems = (activities: any[]) =>
    activities.map((activity: any) => {
        const { title, dotColor, entityLabel } = getActivityDisplay(activity);

        const actor = activity.user || activity.aiAgent;
        const userName = `${actor?.firstName || ''} ${actor?.lastName || ''}`.trim() || actor?.name || activity.integration?.name || 'System';

        const resolvedMsg = resolveActivityMessage(activity.message, activity.msgParams || {});
        const activityChanges = parseActivityChanges(activity.msgParams?.changes);
        const summarySeen = new Set<string>();
        const summaryParts: string[] = [];

        activityChanges.forEach((c: any) => {
            const collapsedEntity = c.field.match(/^(Shipper|Consignee)\b/)?.[1];
            // Shipper/Consignee address sub-fields (Street, City, Postal, etc.) collapse into one
            // "<Entity> Address" summary label — bare "Shipper"/"Consignee" reads as vague on its own.
            const label = collapsedEntity ? `${collapsedEntity} Address` : c.field.match(/^Item\s+\d+/)?.[0] ?? c.field;

            if (summarySeen.has(label)) return;
            summarySeen.add(label);

            if (resolvedMsg && resolvedMsg.toLowerCase().includes(label.toLowerCase())) return;
            summaryParts.push(label);
        });

        // Prefer leading with WHAT changed over restating the title's verb. "<Actor> updated <fields>
        // on <entity> <code>" is more informative at a glance than "<Actor> updated <entity> <code>
        // (<fields>)", where the actual change was buried in a trailing parenthetical.
        let displayMessage: string;

        if (summaryParts.length > 0) {
            const target = activity.referenceId ? `${entityLabel.toLowerCase()} #${activity.referenceId}` : entityLabel.toLowerCase();
            displayMessage = `${userName} updated ${formatFieldList(summaryParts)} on ${target}`;
        } else {
            displayMessage = resolvedMsg && resolvedMsg.trim().toLowerCase() !== title.trim().toLowerCase() ? `${userName} ${resolvedMsg}`.trim() : userName;

            // The dashboard feed spans many quotes/orders at once, so every row needs to be
            // traceable back to a specific one — append the reference code if the message
            // template didn't already include it (e.g. "{{userId}} created the order").
            if (activity.referenceId && !displayMessage.includes(activity.referenceId)) {
                displayMessage = `${displayMessage} for ${entityLabel.toLowerCase()} #${activity.referenceId}`;
            }
        }

        return {
            id: activity.id,
            title,
            // Dashboard-feed variant of the title — the design there names the actor in the
            // heading ("Status Updated by Hana Cruz") while the per-entity dropdowns keep the
            // short title and lead the message with the actor instead.
            titleWithActor: `${title} by ${userName}`,
            dotColor,
            displayMessage,
            timeLabel: formatActivityTime(activity.createdAt),
            // Lets a consumer (e.g. the cross-entity Dashboard feed) turn the reference code
            // embedded in displayMessage into a link — entityId is whichever of masterOrderId /
            // quoteId the activity's own type points at, matching how the backend derives referenceId.
            referenceId: activity.referenceId as string | undefined,
            entityType: entityLabel === 'Order' ? ('order' as const) : ('quote' as const),
            entityId: (entityLabel === 'Order' ? activity.masterOrderId : activity.quoteId) as number | undefined,
        };
    });
